import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { useImpersonation, type ImpersonatedRetailer } from "@/contexts/ImpersonationContext";

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface AdminProfile {
  id: string;
  email: string | null;
  full_name: string | null;
  role: string;
  is_active: boolean;
  demo_viewer?: boolean;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const ENV_CHECKS: { label: string; value: string | undefined }[] = [
  { label: "VITE_SUPABASE_URL",     value: import.meta.env.VITE_SUPABASE_URL },
  { label: "VITE_SUPABASE_ANON_KEY", value: import.meta.env.VITE_SUPABASE_ANON_KEY },
];

const SERVER_SECRETS = [
  { label: "ANTHROPIC_API_KEY",      note: "Used by extract-specs edge function" },
  { label: "AWS_ACCESS_KEY_ID",      note: "Used by S3 asset upload functions" },
  { label: "AWS_SECRET_ACCESS_KEY",  note: "Used by S3 asset upload functions" },
  { label: "S3_BUCKET_NAME",         note: "Target bucket for AR assets" },
];

const INTEGRATIONS = [
  {
    name: "Stripe",
    description: "Payment processing for retailer subscriptions and billing.",
  },
  {
    name: "HubSpot",
    description: "CRM sync for retailer leads and contact management.",
  },
  {
    name: "Resend",
    description: "Transactional email delivery for payment requests and notifications.",
  },
];

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function Section({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-800 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-900/40 transition"
      >
        <span className="font-medium text-gray-100">{title}</span>
        <span className="text-gray-500 text-sm">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-3 border-t border-gray-800">
          {children}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* DevTools section                                                     */
/* ------------------------------------------------------------------ */

interface RetailerOption {
  id: string;
  store_name: string;
  email: string | null;
}

function DevToolsSection({ jwt }: { jwt: string }) {
  const { startImpersonation } = useImpersonation();
  const navigate = useNavigate();

  const [retailers, setRetailers] = useState<RetailerOption[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const loadRetailers = useCallback(async () => {
    if (!jwt || retailers.length > 0) return;
    setLoadingRetailers(true);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/retailers?select=id,store_name,email&order=store_name.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (res.ok) setRetailers(await res.json());
    } finally {
      setLoadingRetailers(false);
    }
  }, [jwt, retailers.length]);

  // Load retailers when section opens
  useEffect(() => {
    if (open) loadRetailers();
  }, [open, loadRetailers]);

  const beginImpersonation = (role: "retailer" | "support") => {
    const retailer = retailers.find((r) => r.id === selectedId) ?? retailers[0];
    if (!retailer) return;
    const imp: ImpersonatedRetailer = {
      id: retailer.id,
      store_name: retailer.store_name,
      email: retailer.email,
    };
    startImpersonation(role, imp);
    navigate("/portal");
  };

  return (
    <Section title="Developer Tools & Impersonation">
      <div className="space-y-5">
        <p className="text-xs text-gray-500">
          Impersonate a retailer account to preview their portal experience.
          All actions are read-only while impersonating — no data will be modified.
        </p>

        {/* Retailer selector */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-300">Select retailer</label>
          {loadingRetailers ? (
            <div className="h-10 rounded-lg bg-gray-800 animate-pulse" />
          ) : (
            <select
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-orange-500 transition"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— Select a retailer —</option>
              {retailers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.store_name}{r.email ? ` (${r.email})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Role preview buttons */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-800 p-4 space-y-2">
            <p className="text-sm font-medium text-gray-200">Retailer Preview</p>
            <p className="text-xs text-gray-500">
              View the portal exactly as the retailer sees it.
            </p>
            <button
              onClick={() => beginImpersonation("retailer")}
              disabled={!selectedId && retailers.length === 0}
              className="w-full rounded-lg bg-orange-600 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
            >
              {selectedId ? "Start Impersonation" : retailers.length > 0 ? "Preview First Retailer" : "No retailers"}
            </button>
          </div>

          <div className="rounded-lg border border-gray-800 p-4 space-y-2">
            <p className="text-sm font-medium text-gray-200">Support View</p>
            <p className="text-xs text-gray-500">
              Support agents view any retailer's portal in read-only mode.
            </p>
            <button
              onClick={() => beginImpersonation("support")}
              disabled={!selectedId && retailers.length === 0}
              className="w-full rounded-lg border border-gray-700 py-2 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
            >
              {selectedId ? "View as Support" : retailers.length > 0 ? "Support First Retailer" : "No retailers"}
            </button>
          </div>
        </div>

        {retailers.length === 0 && !loadingRetailers && (
          <p className="text-xs text-gray-600">
            No retailer accounts found. Create one first via the Retailers page.
          </p>
        )}
      </div>
    </Section>
  );
}

// Re-export Section component used in DevToolsSection above
// (Section is defined below in AdminSettings — forward declare it here via hoisting)

/* ------------------------------------------------------------------ */
/* AdminSettings main                                                   */
/* ------------------------------------------------------------------ */

export default function AdminSettings() {
  const { session } = useAuth();
  const [users, setUsers] = useState<AdminProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    const jwt = session?.access_token;
    if (!jwt) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/profiles?select=id,email,full_name,role,is_active,demo_viewer,created_at&order=created_at.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      setUsers(await res.json());
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Demo viewers: accounts that can see the demo stores in the app (server-side rule; admin-set only) ──
  const [demoBusyId, setDemoBusyId] = useState<string | null>(null);
  const [demoMsg, setDemoMsg] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function toggleDemoViewer(u: AdminProfile, on: boolean) {
    const jwt = session?.access_token;
    if (!jwt) return;
    setDemoBusyId(u.id);
    setDemoMsg(null);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/profiles?id=eq.${u.id}`, {
        method: "PATCH",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ demo_viewer: on }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, demo_viewer: on } : x)));
      setDemoMsg(`${u.email ?? "User"} ${on ? "can now" : "can no longer"} see demo stores (takes effect within a few minutes).`);
    } catch (e) {
      setDemoMsg(`Could not change demo viewer: ${e instanceof Error ? e.message : "error"}`);
    } finally {
      setDemoBusyId(null);
    }
  }

  async function resetDemoData() {
    const jwt = session?.access_token;
    if (!jwt) return;
    setResetting(true);
    setDemoMsg(null);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/rpc/reset_demo_data`, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: "{}",
      });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 120)}`);
      const out = await res.json();
      const seeded = out?.seeded ?? {};
      setDemoMsg(`Demo data reset: ${seeded.stores ?? 7} stores, ${seeded.quotes ?? 0} quote requests, ${seeded.events ?? 0} synthetic events.`);
      setConfirmReset(false);
    } catch (e) {
      setDemoMsg(`Reset failed: ${e instanceof Error ? e.message : "error"}`);
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Settings</h1>

      {/* ── Billing ──────────────────────────────────────────────── */}
      <Section title="Billing" defaultOpen>
        <p className="text-sm text-gray-400 mb-4">
          Stripe integration is planned for v2. Payment requests can currently be sent
          manually via the Retailers page.
        </p>
        <div className="rounded-lg border border-dashed border-gray-700 p-8 text-center space-y-2">
          <p className="text-sm font-medium text-gray-500">Stripe Billing Dashboard</p>
          <p className="text-xs text-gray-600">Coming in v2 — Stripe integration required</p>
        </div>
      </Section>

      {/* ── Team / Users ─────────────────────────────────────────── */}
      <Section title="Team & Users">
        <p className="text-xs text-gray-500 mb-3">
          Role and activation changes via Supabase dashboard for now. The Demo viewer flag can be set here: flagged
          accounts see the demo stores in the app (store list, map, retailer detail); everyone else sees only real stores.
        </p>
        {usersError && (
          <p className="mb-3 text-sm text-red-400">{usersError}</p>
        )}
        {usersLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-gray-800 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60">
                <tr className="border-b border-gray-800">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Demo viewer</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="border-t border-gray-800">
                      <td className="px-3 py-2 text-gray-300">{u.email ?? "—"}</td>
                      <td className="px-3 py-2 text-gray-400">{u.full_name ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-xs font-medium ${
                            u.role === "admin" ? "text-orange-400" : "text-gray-400"
                          }`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-xs ${
                            u.is_active ? "text-green-400" : "text-gray-600"
                          }`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Demo viewer: ${u.email ?? u.id}`}
                          checked={u.demo_viewer === true}
                          disabled={demoBusyId === u.id}
                          onChange={(e) => void toggleDemoViewer(u, e.target.checked)}
                          className="h-4 w-4 accent-amber-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(u.created_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── Demo data ───────────────────────────────── */}
      <Section title="Demo data">
        <p className="text-xs text-gray-500 mb-3">
          The demo sandbox: 7 labelled demo stores, the Demo Hearth &amp; Home portal (mock quote requests and 90 days of
          synthetic metrics) and the demo manufacturer's dashboards. Everything is tagged and kept out of real dashboards,
          analytics, outreach and billing. Reset restores the pristine demo state; it never touches real data.
        </p>
        {demoMsg && <p className="mb-3 text-sm text-amber-300">{demoMsg}</p>}
        <button
          onClick={() => setConfirmReset(true)}
          className="rounded-lg border border-amber-700 bg-amber-900/20 px-4 py-2 text-sm font-medium text-amber-300 hover:bg-amber-900/40 transition"
        >
          Reset demo data
        </button>
      </Section>
      {confirmReset && (
        <DeleteConfirmDialog
          title="Reset demo data?"
          description="Deletes all demo quote requests, demo pairings and synthetic events, then rebuilds them from the seed. Real data is not touched."
          confirmLabel="Reset demo data"
          loadingLabel="Resetting…"
          loading={resetting}
          onConfirm={resetDemoData}
          onCancel={() => setConfirmReset(false)}
        />
      )}

      {/* ── Integrations ─────────────────────────────────────────── */}
      <Section title="Integrations">
        <div className="grid gap-3 sm:grid-cols-3">
          {INTEGRATIONS.map((intg) => (
            <div
              key={intg.name}
              className="rounded-lg border border-gray-800 p-4 space-y-2"
            >
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-200">{intg.name}</p>
                <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs text-gray-500">
                  v2
                </span>
              </div>
              <p className="text-xs text-gray-500">{intg.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Developer Tools ──────────────────────────────────────── */}
      <DevToolsSection jwt={session?.access_token ?? ""} />

      {/* ── API Keys ─────────────────────────────────────────────── */}
      <Section title="API Keys & Environment">
        <div className="space-y-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
              Browser Variables
            </p>
            <div className="space-y-2">
              {ENV_CHECKS.map(({ label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-lg border border-gray-800 px-3 py-2"
                >
                  <span className="text-sm font-mono text-gray-300">{label}</span>
                  <span
                    className={`flex items-center gap-1.5 text-xs font-medium ${
                      value ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full ${
                        value ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    {value ? "Configured" : "Missing"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
              Supabase Secrets (server-side)
            </p>
            <div className="space-y-2">
              {SERVER_SECRETS.map(({ label, note }) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-lg border border-gray-800 px-3 py-2"
                >
                  <div>
                    <span className="text-sm font-mono text-gray-300">{label}</span>
                    {note && (
                      <p className="text-xs text-gray-600 mt-0.5">{note}</p>
                    )}
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-600" />
                    Server-side
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
